import React, {useState} from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Navigate } from 'react-router-dom';

import { api, getApiUrl, ResponseError } from 'utils/api';
import { PostUserLoginResponse } from 'utils/api/users';
import usePrefix from 'utils/usePrefix';
import { notificationTypes } from 'utils/constants';
import { userRoles } from 'utils/userRoles';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import { SET_PASSWORD_URL } from 'App/constants';

import LoginWrapper from 'components/LoginWrapper';
import Language from 'components/Language';
import Link from 'components/Link';
import colors from 'styles/colors';

import Form from './Form';
import { AccountBlockedMessageStyled, LoginFormActionsStyled } from './styles';

const url = getApiUrl('/users/login');

const validationSchema = (ty: (key: string, vars?: object) => string) =>
  Yup.object().shape({
    email: Yup.string().email(ty('invalid_email')).required(ty('field_required')),
    password: Yup.string().required(ty('field_required')),
  });

interface FormValues {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const [{ profile }, dispatch] = useApp();
  const [isAccountLocked, setAccountLocked] = useState(false);
  const [emailAddress, setEmail] = useState('');

  const t = usePrefix('Login');
  const ty = usePrefix('YupErrors');

  const submit = async (values: FormValues) => {
    try {
      const response: PostUserLoginResponse = await api(url, {
        method: 'POST',
        payload: values,
      });

      if (response) {
        dispatch({ type: ActionTypes.LOGIN, payload: response });
      }
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
      if (typedError?.parsed?.code === ActionTypes.TO_MANY_USER_LOGIN_ATTEMPTS) {
        setAccountLocked(true);
        setEmail(values.email);
      } else {
        setAccountLocked(false);
        setEmail('');
      }
    }

    return emailAddress;
  };

  if (profile) {
    if (profile.role === userRoles.intervener) return <Navigate to="/app/chat" />;
    return <Navigate to="/app/reports" />;
  }

  return (
    <>
      <LoginWrapper>
        <Formik
          initialValues={{ email: '', password: '' }}
          onSubmit={submit}
          validationSchema={() => validationSchema(ty)}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form errors={errors} touched={touched} isSubmitting={isSubmitting} />
          )}
        </Formik>
        {isAccountLocked && (<AccountBlockedMessageStyled>{ty('account_blocked_after_unsuccessful_attempts', { email: emailAddress })}</AccountBlockedMessageStyled>)}
        <LoginFormActionsStyled>
          <Link to={`/app/${SET_PASSWORD_URL}`} color={colors.brand_2}>
            {t('forgotPassword')}
          </Link>
        </LoginFormActionsStyled>
        <Language />
      </LoginWrapper>
    </>
  );
};

export default Login;
